/**
 * StatsService
 * Fetches pre-aggregated scan statistics from the backend.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';

export interface UserStats {
  total_scans: number;
  total_value: number;
  criteria_stats: Record<string, Record<string, number>>;
  last_scan_at: string | null;
}

export class StatsService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 30000, // Slow path (S3 backfill) can take several seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Attach JWT token to every request
    this.axiosInstance.interceptors.request.use(
      async (cfg) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            cfg.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('StatsService: failed to get access token:', error);
        }
        return cfg;
      },
      (error) => Promise.reject(error)
    );

    // Log out on 401
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.error('StatsService: authentication failed');
          await this.authService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch pre-aggregated statistics for the current user.
   *
   * Fast path: O(1) DynamoDB read when a stats record exists.
   * Slow path (first call): server computes from S3 and returns; subsequent
   * calls are instant.
   *
   * @returns Promise resolving to UserStats
   * @throws Error if the request fails
   */
  async getStats(): Promise<UserStats> {
    try {
      const response = await this.axiosInstance.get<UserStats>('/api/stats');
      return response.data;
    } catch (error) {
      console.error('StatsService: failed to fetch stats:', error);
      throw this.handleError(error, 'Failed to fetch statistics');
    }
  }

  private handleError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please check your connection.');
      }
      if (axiosError.code === 'ERR_NETWORK') {
        return new Error('Network error. Please check your connection.');
      }
      if (axiosError.response) {
        const status = axiosError.response.status;
        switch (status) {
          case 401:
            return new Error('Authentication failed. Please log in again.');
          case 403:
            return new Error('You do not have permission to view statistics.');
          case 500:
          case 502:
          case 503:
            return new Error('Server error. Please try again later.');
          default:
            return new Error(`${defaultMessage} (Status: ${status})`);
        }
      }
      return new Error('Network error. Please check your connection.');
    }
    if (error instanceof Error) return error;
    return new Error(defaultMessage);
  }
}

export default StatsService;
